import type { UseQueryOptions } from "react-query";

import {
  useGetCepWorkspaceConfigQuery,
  type GetCepWorkspaceConfigQuery,
} from "src/graphql";

/**
 * The workspace's CEP config. `cep_workspace_config` is unique per workspace,
 * so this unwraps the single row from the Hasura list result. `isSuccess`
 * separates a workspace that has no config from one whose config has not
 * arrived: both leave `config` undefined. `isError` is terminal until
 * `refetch`, since queries do not retry.
 *
 * `refetchInterval` is passed through so callers watching a background job
 * (e.g. a queued top-K refresh on the locale column) can poll this query while
 * the job is outstanding. Use `useGovernedRefetchInterval` to produce it.
 */
export function useCepWorkspaceConfig({
  enabled = true,
  refetchInterval,
}: {
  enabled?: boolean;
  refetchInterval?: UseQueryOptions<GetCepWorkspaceConfigQuery>["refetchInterval"];
} = {}) {
  const { data, isLoading, isSuccess, isError, refetch } =
    useGetCepWorkspaceConfigQuery(undefined, { enabled, refetchInterval });
  return {
    config: data?.cep_workspace_config?.[0],
    isLoading,
    isSuccess,
    isError,
    refetch,
  };
}
